import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  doLockUnlock,
  doSetWifi,
  setStatusNotification,
  updateStatusNotification,
  deleteStatusNotification,
  getSingleDevice,
  updatePivotOverviewPageSingleSelectedDevice
} from 'redux/actions';
import { isDeviceLocked, isDeviceWiFiEnabled } from 'common/utils/deviceTwinUtils';
import { IconLoader } from 'common/IconLoader';
import { useDispatch } from 'react-redux';
import { Status } from 'common/utils/constants';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import LockUnlockModal from '../LockUnlockModal/LockUnlockModal';
import BundleOverviewControllerHub from './BundleOverviewControllerHub/BundleOverviewControllerHub';
import { getDeviceSwVersion } from 'common/utils/helpers';
import BundleOverviewControllerDUTab from './BundleOverviewControllerDUTab/BundleOverviewControllerDUTab';
import BundleOverviewControllerSUTab from './BundleOverviewControllerSUTab/BundleOverviewControllerSUTab';
import './BundleOverviewController.css';

const SCROLLER_OFFSET = 32;

const BundleOverviewController = ({
  device,
  bundle,
  deviceType,
  serviceDevices,
  bundleMembers,
  isGa2,
  sendSelectedDevice,
  latestActiveAction
}) => {
  const scrollerRef = useRef(null);
  const containerRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const dispatch = useDispatch();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const curDetailDeviceId = searchParams.get('detail');
  const [activeIndex, setActiveIndex] = useState(null);
  const [isLockUnlockModalOpen, setIsLockUnlockModalOpen] = useState(false);
  const [targetDevice, setTargetDevice] = useState(null);
  const [scrollButtons, setScrollButtons] = useState({ start: false, end: false });

  const donorSwVersion = getDeviceSwVersion(device);

  const updateScrollButtons = useCallback(() => {
    const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
    const start = scrollLeft > 1;
    const end = scrollLeft < scrollWidth - clientWidth - 1;

    setScrollButtons((prev) => {
      if (prev.start !== start || prev.end !== end) {
        return { start, end };
      }

      return prev;
    });
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    scrollTimerRef.current = setTimeout(() => {
      updateScrollButtons();
    }, 50);
  }, [updateScrollButtons]);

  const handleScrollTo = useCallback((index) => {
    const scroller = scrollerRef.current;
    const container = containerRef.current;
    const { scrollLeft, clientWidth } = scroller;
    const tabs = Array.from(container.children);

    let nextScrollLeft = 0;

    for (let i = 0; i < index; i += 1) {
      nextScrollLeft += tabs[i].clientWidth;
    }

    if (scrollLeft > nextScrollLeft) {
      scroller.scrollLeft = nextScrollLeft - tabs[index].clientWidth / 2;
    }

    if (scrollLeft + clientWidth - 2 * SCROLLER_OFFSET < nextScrollLeft + tabs[index].clientWidth) {
      scroller.scrollLeft = nextScrollLeft + tabs[index].clientWidth / 2;
    }
  }, []);

  const handleScrollToStart = useCallback(() => {
    const scroller = scrollerRef.current;
    const container = containerRef.current;
    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    const tabs = Array.from(container.children);
    const maxScroll = scrollWidth - clientWidth;
    const maxScrollLeft = scrollLeft - clientWidth + 2 * SCROLLER_OFFSET;
    let nextScrollLeft;

    if (maxScrollLeft > 0) {
      nextScrollLeft = maxScroll;

      for (let i = tabs.length - 1; i >= 0; i -= 1) {
        if (nextScrollLeft - tabs[i].clientWidth < maxScrollLeft) {
          nextScrollLeft -= tabs[i].clientWidth;
        } else {
          break;
        }
      }
    } else {
      nextScrollLeft = 0;
    }

    scroller.scrollLeft = nextScrollLeft;
  }, []);

  const handleScrollToEnd = useCallback(() => {
    const scroller = scrollerRef.current;
    const container = containerRef.current;
    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    const tabs = Array.from(container.children);
    const maxScroll = scrollWidth - clientWidth;
    const maxScrollLeft = scrollLeft + clientWidth - 2 * SCROLLER_OFFSET;
    let nextScrollLeft;

    if (maxScrollLeft < maxScroll) {
      nextScrollLeft = scrollWidth - clientWidth;

      for (let i = 0; i < tabs.length; i += 1) {
        if (nextScrollLeft + tabs[i].clientWidth < maxScrollLeft) {
          nextScrollLeft += tabs[i].clientWidth;
        } else {
          break;
        }
      }
    } else {
      nextScrollLeft = maxScroll;
    }

    scroller.scrollLeft = nextScrollLeft;
  }, []);

  const handleWiFiAction = useCallback(
    (device) => {
      let wifiMode = isDeviceWiFiEnabled(device) ? 'disabled' : 'enabled';
      dispatch(
        setStatusNotification({
          id: 'wifiMode',
          text: wifiMode === 'enabled' ? 'Enabling Wi-Fi...' : 'Disabling Wi-Fi...',
          status: Status.PENDING,
          hideCloseButton: true
        })
      );

      const setWiFiOnSuccess = () => {
        dispatch(
          updateStatusNotification({
            id: 'wifiMode',
            text: wifiMode === 'enabled' ? 'Wi-Fi Enabled' : 'Wi-Fi Disabled',
            icon: wifiMode === 'enabled' ? IconLoader.icon_wifi_enabled : IconLoader.icon_wifi_disabled,
            status: Status.SUCCESS
          })
        );
        dispatch(
          getSingleDevice(device?.deviceId, (result) => {
            dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
          })
        );
        setTimeout(() => {
          dispatch(deleteStatusNotification({ id: 'wifiMode' }));
        }, 3000);
      };

      const setWiFiOnError = (error) => {
        dispatch(
          updateStatusNotification({
            id: 'wifiMode',
            text: error.message ?? '',
            icon: IconLoader.warning_circle,
            status: Status.FAILURE
          })
        );
      };

      dispatch(doSetWifi(device?.deviceId, wifiMode, null, null, null, null, setWiFiOnSuccess, setWiFiOnError));
    },
    [dispatch]
  );

  const handleLockUnlockAction = useCallback(
    (device) => {
      const isLocked = isDeviceLocked(device);
      dispatch(
        setStatusNotification({
          id: isLocked ? 'unlock' : 'lock',
          text: isLocked ? 'Unlocking the device...' : 'Locking the device...',
          status: Status.PENDING,
          hideCloseButton: true
        })
      );

      dispatch(
        doLockUnlock(
          device?.deviceId,
          isLocked ? 'unlock' : 'lock',
          () => {
            dispatch(
              updateStatusNotification({
                id: isLocked ? 'unlock' : 'lock',
                text: `The device is ${isLocked ? 'unlocked' : 'locked'}.`,
                status: Status.SUCCESS,
                icon: isLocked ? IconLoader.icon_unlocked_status : IconLoader.icon_locked_status
              })
            );
            dispatch(
              getSingleDevice(device?.deviceId, (result) => {
                dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
              })
            );
            setTimeout(() => {
              dispatch(deleteStatusNotification({ id: isLocked ? 'unlock' : 'lock' }));
            }, 3000);
          },
          (error) => {
            dispatch(
              updateStatusNotification({
                id: isLocked ? 'unlock' : 'lock',
                text: error.message ?? '',
                status: Status.FAILURE
              })
            );
          }
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(scrollerRef.current);
      resizeObserver.observe(containerRef.current);
    } else {
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    updateScrollButtons();
  }, [updateScrollButtons]);

  useEffect(
    () => () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (curDetailDeviceId && !activeIndex) {
      const allDevices = [device, ...(serviceDevices || [])];
      const detailDevice = allDevices?.find((item) => item?.id === curDetailDeviceId);
      if (detailDevice) {
        sendSelectedDevice(detailDevice);
        setTargetDevice(detailDevice);
        setActiveIndex(curDetailDeviceId);
      }
    }
  }, [curDetailDeviceId, activeIndex, serviceDevices, device, sendSelectedDevice]);

  return (
    <div id="BundleOverviewControllerContainer" className="BundleOverviewControllerContainer">
      {isLockUnlockModalOpen && (
        <LockUnlockModal
          device={targetDevice}
          close={() => setIsLockUnlockModalOpen(false)}
          action={handleLockUnlockAction}
        />
      )}
      <BundleOverviewControllerHub
        device={device}
        bundle={bundle}
        isGa2={isGa2}
        bundleMembers={bundleMembers}
        deviceType={deviceType}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        sendSelectedDevice={sendSelectedDevice}
        latestActiveAction={latestActiveAction}
      />
      <div className="ScrollableTabs">
        {scrollButtons.start && (
          <div className="ScrollableTabsControl ScrollableTabsControlStart">
            <SvgAF
              customClass="ScrollableTabsControlIcon"
              img={IconLoader.icon_dropdown_arrow}
              hover={IconLoader.icon_dropdown_arrow_hover}
              onClick={handleScrollToStart}
            />
          </div>
        )}
        <div ref={scrollerRef} className="ScrollableTabsScroller" onScroll={handleScroll}>
          <div ref={containerRef} className="ScrollableTabsContainer">
            <BundleOverviewControllerDUTab
              id={0}
              key={0}
              device={device}
              isGa2={isGa2}
              donorSwVersion={donorSwVersion}
              deviceType={deviceType}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              sendSelectedDevice={sendSelectedDevice}
              setTargetDevice={setTargetDevice}
              handleLockUnlockAction={handleLockUnlockAction}
              setIsLockUnlockModalOpen={setIsLockUnlockModalOpen}
              handleWiFiAction={handleWiFiAction}
              scrollTo={handleScrollTo}
            />

            {serviceDevices?.map((su, index) => (
              <BundleOverviewControllerSUTab
                id={index + 1}
                key={index + 1}
                donorDevice={device}
                device={su}
                isGa2={isGa2}
                deviceType={deviceType}
                isServiceUnit={true}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                sendSelectedDevice={sendSelectedDevice}
                setTargetDevice={setTargetDevice}
                scrollTo={handleScrollTo}
                donorSwVersion={donorSwVersion}
                handleLockUnlockAction={handleLockUnlockAction}
                setIsLockUnlockModalOpen={setIsLockUnlockModalOpen}
                handleWiFiAction={handleWiFiAction}
              />
            ))}
          </div>
        </div>
        {scrollButtons.end && (
          <div className="ScrollableTabsControl ScrollableTabsControlEnd">
            <SvgAF
              customClass="ScrollableTabsControlIcon"
              img={IconLoader.icon_dropdown_arrow}
              hover={IconLoader.icon_dropdown_arrow_hover}
              onClick={handleScrollToEnd}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleOverviewController;
